import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { s3BucketsApi } from '../services/s3-buckets-api';

interface S3ImportDialogProps {
  open: boolean;
  onClose: () => void;
  bucketId: string;
  environmentId: string;
  bucketName: string;
  environmentName: string;
  onSuccess?: () => void;
}

export const S3ImportDialog: React.FC<S3ImportDialogProps> = ({
  open,
  onClose,
  bucketId,
  environmentId,
  bucketName,
  environmentName,
  onSuccess,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Metadata fields
  const [activityName, setActivityName] = useState<string>('');
  const [activityId, setActivityId] = useState<string>('');
  const [tenantId, setTenantId] = useState<string>('');
  const [courseId, setCourseId] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const metadata: any = {};
      if (activityName) metadata.activityName = activityName;
      if (activityId) metadata.activityId = activityId;
      if (tenantId) metadata.tenantId = tenantId;
      if (courseId) metadata.courseId = courseId;

      const result = await s3BucketsApi.importPackage(
        bucketId,
        environmentId,
        selectedFile,
        Object.keys(metadata).length > 0 ? metadata : undefined
      );

      setSuccess(`Package uploaded successfully to ${result.s3Key}`);

      // Call onSuccess callback after a short delay
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 2000);
      } else {
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload package');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFile(null);
      setActivityName('');
      setActivityId('');
      setTenantId('');
      setCourseId('');
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Upload CMI5 Package to S3
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
            Uploading to: <strong>{bucketName} / {environmentName}</strong>
          </Alert>

          {/* File Selection */}
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,.tar.gz"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              fullWidth
            >
              {selectedFile ? selectedFile.name : 'Select Package File (.zip or .tar.gz)'}
            </Button>
            {selectedFile && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Size: {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
              </Typography>
            )}
          </Box>

          {/* Metadata Fields */}
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Package Metadata (Optional)
          </Typography>

          <TextField
            fullWidth
            label="Activity Name"
            value={activityName}
            onChange={(e) => setActivityName(e.target.value)}
            helperText="Display name for the activity"
            size="small"
          />

          <TextField
            fullWidth
            label="Activity ID"
            value={activityId}
            onChange={(e) => setActivityId(e.target.value)}
            helperText="Unique identifier for the activity"
            size="small"
          />

          <TextField
            fullWidth
            label="Tenant ID"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            helperText="Tenant/organization identifier"
            size="small"
          />

          <TextField
            fullWidth
            label="Course ID"
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            helperText="Course identifier"
            size="small"
          />

          <Alert severity="info" sx={{ fontSize: '0.75rem', mt: 1 }}>
            Metadata will be stored with the S3 object and can be used for organizing and searching packages.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!selectedFile || uploading || !!success}
          startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};
